<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import AppIcon from '@/components/utility/icons/index.vue';
import { useAppStore } from '@/store/app/app.js';
import { lmnFetch } from '@/assets/js/functions/lmnFetch.js';
import { encodeToHash } from '@/assets/js/functions/encodeToHash.js';
import {
  setupColorScheme,
  useColorScheme,
} from '@/assets/js/functions/useColorScheme.js';

setupColorScheme();

const appStore = useAppStore();
const { setColorScheme, userPreference } = useColorScheme();

const isOpen = ref(false);
const triggerRef = ref(null);
const dropdownRef = ref(null);
const gravatar = ref('');
const dropdownPosition = ref({ top: 0, left: 0 });

const userMenuItems = computed(() => {
  const tabs = [
    {
      id: 'profile',
      name: __('Edit Profile', 'uixpress'),
      icon: 'person',
      url: `${appStore.state.adminUrl}profile.php`,
    },
    {
      id: 'view-site',
      name: __('Visit site', 'uixpress'),
      icon: 'home',
      url: `${appStore.state.siteURL}`,
    },
    {
      id: 'site-settings',
      name: __('Site Settings', 'uixpress'),
      icon: 'settings',
      url: `${appStore.state.adminUrl}options-general.php`,
    },
    {
      id: 'logout',
      name: __('Sign Out', 'uixpress'),
      icon: 'logout',
      action: logoutUser,
      destructive: true,
    },
  ];

  if (!appStore.state.userRoles?.includes('administrator')) {
    tabs.splice(2, 1);
  }

  return tabs;
});

async function setGravatar() {
  if (!appStore.state.userEmail) return;
  const hashHex = await encodeToHash(appStore.state.userEmail);
  gravatar.value = `https://gravatar.com/avatar/${hashHex}?d=blank`;
}

async function logoutUser() {
  const args = { endpoint: 'uixpress/v1/logout', params: {}, type: 'POST' };
  await lmnFetch(args);
  window.location.href = appStore.state.loginUrl;
}

function closeDropdown() {
  isOpen.value = false;
}

function handleOutsideClick(event) {
  if (!isOpen.value) return;
  if (
    triggerRef.value?.contains(event.target) ||
    dropdownRef.value?.contains(event.target)
  ) {
    return;
  }
  closeDropdown();
}

function handleEscape(event) {
  if (event.key === 'Escape') {
    closeDropdown();
  }
}

async function updatePosition() {
  if (!triggerRef.value || !dropdownRef.value) return;

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const dropdownRect = dropdownRef.value.getBoundingClientRect();

  let left = triggerRect.right - dropdownRect.width;
  let top = triggerRect.bottom + 8;

  if (left < 8) left = 8;
  if (left + dropdownRect.width > window.innerWidth - 8) {
    left = window.innerWidth - dropdownRect.width - 8;
  }

  dropdownPosition.value = { top, left };
}

async function toggleDropdown() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await nextTick();
    updatePosition();
  }
}

function handleMenuAction(item) {
  if (item.action) {
    item.action();
  } else if (item.url) {
    window.location.href = item.url;
  }
  closeDropdown();
}

onMounted(() => {
  setGravatar();
  document.addEventListener('mousedown', handleOutsideClick, true);
  document.addEventListener('keydown', handleEscape, true);
  window.addEventListener('resize', updatePosition);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideClick, true);
  document.removeEventListener('keydown', handleEscape, true);
  window.removeEventListener('resize', updatePosition);
});
</script>

<template>
  <div id="uixpress-frontend-user" ref="triggerRef">
    <button
      id="uixpress-frontend-user-trigger"
      type="button"
      :aria-expanded="isOpen ? 'true' : 'false'"
      @click="toggleDropdown"
    >
      <span id="uixpress-frontend-user-initial">{{
        appStore.state.userName?.charAt(0) || 'U'
      }}</span>
      <img
        v-if="gravatar"
        id="uixpress-frontend-user-image"
        :src="gravatar"
        alt=""
      />
    </button>
  </div>

  <Teleport to="body">
    <div
      v-if="isOpen"
      id="uixpress-frontend-user-dropdown"
      ref="dropdownRef"
      :style="`top:${dropdownPosition.top}px;left:${dropdownPosition.left}px;`"
    >
      <div id="uixpress-frontend-user-header">
        <div id="uixpress-frontend-user-header-avatar">
          <span>{{ appStore.state.userName?.charAt(0) || 'U' }}</span>
          <img
            v-if="gravatar"
            id="uixpress-frontend-user-header-image"
            :src="gravatar"
            alt=""
          />
        </div>
        <div id="uixpress-frontend-user-header-meta">
          <div id="uixpress-frontend-user-name">
            {{ appStore.state.userName }}
          </div>
          <div id="uixpress-frontend-user-email">
            {{ appStore.state.userEmail }}
          </div>
        </div>
      </div>

      <div id="uixpress-frontend-user-actions">
        <button
          v-for="item in userMenuItems"
          :key="item.id"
          type="button"
          class="uixpress-frontend-user-action"
          :class="item.destructive ? 'is-destructive' : ''"
          @click="handleMenuAction(item)"
        >
          <span class="uixpress-frontend-user-action-icon">
            <AppIcon :icon="item.icon" />
          </span>
          <span>{{ item.name }}</span>
        </button>
      </div>

      <div id="uixpress-frontend-user-footer">
        <span id="uixpress-frontend-user-footer-label">{{
          __('Theme', 'uixpress')
        }}</span>
        <div id="uixpress-frontend-user-theme-switcher">
          <button
            type="button"
            class="uixpress-frontend-user-theme-button"
            :class="userPreference === 'light' ? 'is-active' : ''"
            :title="__('Light Mode', 'uixpress')"
            @click="setColorScheme('light')"
          >
            <AppIcon icon="light_mode" />
          </button>
          <button
            type="button"
            class="uixpress-frontend-user-theme-button"
            :class="userPreference === 'dark' ? 'is-active' : ''"
            :title="__('Dark Mode', 'uixpress')"
            @click="setColorScheme('dark')"
          >
            <AppIcon icon="dark_mode" />
          </button>
          <button
            type="button"
            class="uixpress-frontend-user-theme-button"
            :class="userPreference === 'system' ? 'is-active' : ''"
            :title="__('System', 'uixpress')"
            @click="setColorScheme('system')"
          >
            <AppIcon icon="settings" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
#uixpress-frontend-user,
#uixpress-frontend-user * {
  box-sizing: border-box !important;
}

#uixpress-frontend-user-trigger {
  width: 28px !important;
  min-width: 28px !important;
  height: 28px !important;
  min-height: 28px !important;
  border: 0 !important;
  border-radius: 14px !important;
  margin: 0 !important;
  padding: 0 !important;
  background: #374151 !important;
  color: #ffffff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: relative !important;
  overflow: hidden !important;
  cursor: pointer !important;
}

#uixpress-frontend-user-trigger:hover {
  filter: brightness(1.05) !important;
}

#uixpress-frontend-user-initial {
  font-size: 12px !important;
  line-height: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
}

#uixpress-frontend-user-image {
  position: absolute !important;
  inset: 0 !important;
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
  border: 0 !important;
  border-radius: 14px !important;
  margin: 0 !important;
  padding: 0 !important;
  float: none !important;
  object-fit: cover !important;
}

#uixpress-frontend-user-dropdown,
#uixpress-frontend-user-dropdown * {
  box-sizing: border-box !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial,
    sans-serif !important;
}

#uixpress-frontend-user-dropdown {
  position: fixed !important;
  width: 260px !important;
  max-width: calc(100vw - 16px) !important;
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  z-index: 999999 !important;
  overflow: hidden !important;
}

#uixpress-frontend-user-header {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  padding: 14px 16px !important;
  border-bottom: 1px solid #f0f1f3 !important;
}

#uixpress-frontend-user-header-avatar {
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  border-radius: 16px !important;
  background: #374151 !important;
  color: #ffffff !important;
  position: relative !important;
  overflow: hidden !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  line-height: 13px !important;
}

#uixpress-frontend-user-header-image {
  position: absolute !important;
  inset: 0 !important;
  width: 32px !important;
  height: 32px !important;
  border: 0 !important;
  border-radius: 16px !important;
  margin: 0 !important;
  padding: 0 !important;
  float: none !important;
  object-fit: cover !important;
}

#uixpress-frontend-user-header-meta {
  min-width: 0 !important;
}

#uixpress-frontend-user-name {
  color: #111827 !important;
  font-size: 15px !important;
  line-height: 18px !important;
  font-weight: 600 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  margin: 0 0 2px 0 !important;
}

#uixpress-frontend-user-email {
  color: #6b7280 !important;
  font-size: 13px !important;
  line-height: 16px !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

#uixpress-frontend-user-actions {
  padding: 6px 0 !important;
  border-bottom: 1px solid #f0f1f3 !important;
}

.uixpress-frontend-user-action {
  width: 100% !important;
  border: 0 !important;
  background: transparent !important;
  color: #374151 !important;
  cursor: pointer !important;
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  text-align: left !important;
  padding: 12px 16px !important;
  margin: 0 !important;
  font-size: 14px !important;
  line-height: 18px !important;
  font-weight: 500 !important;
}

.uixpress-frontend-user-action:hover {
  background: #f8fafc !important;
}

.uixpress-frontend-user-action.is-destructive {
  color: #dc2626 !important;
}

.uixpress-frontend-user-action-icon {
  width: 16px !important;
  min-width: 16px !important;
  height: 16px !important;
  min-height: 16px !important;
  opacity: 0.75 !important;
}

.uixpress-frontend-user-action-icon > span {
  width: 16px !important;
  height: 16px !important;
  min-width: 16px !important;
  min-height: 16px !important;
}

#uixpress-frontend-user-footer {
  padding: 12px 16px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
}

#uixpress-frontend-user-footer-label {
  color: #4b5563 !important;
  font-size: 13px !important;
  line-height: 16px !important;
  font-weight: 600 !important;
}

#uixpress-frontend-user-theme-switcher {
  display: flex !important;
  align-items: center !important;
  gap: 2px !important;
  background: #f3f4f6 !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 999px !important;
  padding: 2px !important;
}

.uixpress-frontend-user-theme-button {
  width: 26px !important;
  min-width: 26px !important;
  height: 26px !important;
  min-height: 26px !important;
  border: 0 !important;
  border-radius: 999px !important;
  background: transparent !important;
  color: #4b5563 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
  padding: 0 !important;
  cursor: pointer !important;
}

.uixpress-frontend-user-theme-button:hover {
  background: #e5e7eb !important;
}

.uixpress-frontend-user-theme-button.is-active {
  background: #ffffff !important;
  color: #111827 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08) !important;
}

.uixpress-frontend-user-theme-button > span {
  width: 14px !important;
  height: 14px !important;
  min-width: 14px !important;
  min-height: 14px !important;
}
</style>
